import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  format,
  isValid,
  parseISO,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";

const METRIC_KEYS = ["spendUSD", "estimatedSpendUSD", "impressions", "click"];

const roundTo = ({ value, decimals }) => {
  const factor = 10 ** decimals;
  return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const emptyMetrics = () => ({
  spendUSD: 0,
  estimatedSpendUSD: 0,
  impressions: 0,
  click: 0,
});

// Noon avoids DST transitions at local midnight (e.g. America/Santiago), which
// otherwise shift parsed days to 01:00 and break day-by-day iteration.
const parseDateKey = (dateKey) => parseISO(`${dateKey}T12:00:00`);

export const toDateKey = (date) => format(date, "yyyy-MM-dd");

const toMonthKey = (dateKey) => String(dateKey).slice(0, 7);

export const toDateKeyOrNull = (value) => {
  if (value == null || value === "") return null;
  const date =
    typeof value === "string" ? parseISO(value) : new Date(value.valueOf());
  return isValid(date) ? toDateKey(date) : null;
};

const normalizeCustomRange = (customRange) => {
  const from = customRange?.from;
  const to = customRange?.to;
  if (!from || !to) return null;
  return from > to ? { from: to, to: from } : { from, to };
};

export const resolvePresetRange = ({ preset, today, customRange }) => {
  if (preset === "historic") return null;
  if (preset === "custom") return normalizeCustomRange(customRange);
  if (!today) return null;

  const todayDate = parseDateKey(today);
  if (preset === "last_7") {
    return { from: toDateKey(subDays(todayDate, 6)), to: today };
  }
  if (preset === "last_30") {
    return { from: toDateKey(subDays(todayDate, 29)), to: today };
  }
  if (preset === "last_90") {
    return { from: toDateKey(subDays(todayDate, 89)), to: today };
  }
  if (preset === "this_month") {
    return { from: toDateKey(startOfMonth(todayDate)), to: today };
  }
  if (preset === "last_month") {
    const previousMonth = subMonths(todayDate, 1);
    return {
      from: toDateKey(startOfMonth(previousMonth)),
      to: toDateKey(endOfMonth(previousMonth)),
    };
  }
  return null;
};

const monthEndKey = (monthKey) =>
  toDateKey(endOfMonth(parseDateKey(`${monthKey}-01`)));

export const buildRangeReadPlan = ({ from, to }) => {
  if (!from || !to || from > to) {
    return { monthRange: null, dailyRanges: [] };
  }

  const fullMonths = [];
  let cursor = parseDateKey(`${toMonthKey(from)}-01`);
  const endCursor = parseDateKey(`${toMonthKey(to)}-01`);

  while (cursor <= endCursor) {
    const monthKey = format(cursor, "yyyy-MM");
    const monthStart = `${monthKey}-01`;
    const monthEnd = monthEndKey(monthKey);
    if (monthStart >= from && monthEnd <= to) {
      fullMonths.push(monthKey);
    }
    cursor = addMonths(cursor, 1);
  }

  if (fullMonths.length === 0) {
    return { monthRange: null, dailyRanges: [{ from, to }] };
  }

  const monthRange = {
    from: fullMonths[0],
    to: fullMonths[fullMonths.length - 1],
  };
  const dailyRanges = [];
  const firstFullStart = `${fullMonths[0]}-01`;

  if (from < firstFullStart) {
    dailyRanges.push({
      from,
      to: toDateKey(subDays(parseDateKey(firstFullStart), 1)),
    });
  }

  const lastFullEnd = monthEndKey(fullMonths[fullMonths.length - 1]);
  if (to > lastFullEnd) {
    dailyRanges.push({
      from: toDateKey(addDays(parseDateKey(lastFullEnd), 1)),
      to,
    });
  }

  return { monthRange, dailyRanges };
};

export const createEmptyRollup = () => ({
  totals: emptyMetrics(),
  byPlatform: {},
  byAdvertiser: {},
  byCountry: {},
});

const addMetrics = ({ target, source }) => {
  METRIC_KEYS.forEach((key) => {
    target[key] = toNumber(target[key]) + toNumber(source?.[key]);
  });
};

// Nested byPlatform maps (byAdvertiser / byCountry) hold plain USD numbers.
const addSpendPlatformMap = ({ target, source }) => {
  Object.entries(source || {}).forEach(([platform, spendUSD]) => {
    target[platform] = toNumber(target[platform]) + toNumber(spendUSD);
  });
};

export const mergeRollups = ({ rollups }) => {
  const merged = createEmptyRollup();

  (rollups || []).forEach((rollup) => {
    if (!rollup) return;

    addMetrics({ target: merged.totals, source: rollup.totals });

    Object.entries(rollup.byPlatform || {}).forEach(([platform, metrics]) => {
      if (!merged.byPlatform[platform]) {
        merged.byPlatform[platform] = emptyMetrics();
      }
      addMetrics({ target: merged.byPlatform[platform], source: metrics });
    });

    Object.entries(rollup.byAdvertiser || {}).forEach(
      ([advertiserID, entry]) => {
        if (!merged.byAdvertiser[advertiserID]) {
          merged.byAdvertiser[advertiserID] = {
            spendUSD: 0,
            estimatedSpendUSD: 0,
            byPlatform: {},
          };
        }
        const target = merged.byAdvertiser[advertiserID];
        target.spendUSD += toNumber(entry?.spendUSD);
        target.estimatedSpendUSD += toNumber(entry?.estimatedSpendUSD);
        addSpendPlatformMap({
          target: target.byPlatform,
          source: entry?.byPlatform,
        });
      }
    );

    Object.entries(rollup.byCountry || {}).forEach(([country, entry]) => {
      if (!merged.byCountry[country]) {
        merged.byCountry[country] = { spendUSD: 0, byPlatform: {} };
      }
      const target = merged.byCountry[country];
      target.spendUSD += toNumber(entry?.spendUSD);
      addSpendPlatformMap({
        target: target.byPlatform,
        source: entry?.byPlatform,
      });
    });
  });

  return merged;
};

export const getPreviousPeriod = ({ from, to }) => {
  if (!from || !to || from > to) return null;

  const start = parseDateKey(from);
  const end = parseDateKey(to);
  const days = differenceInCalendarDays(end, start) + 1;
  const previousEnd = subDays(start, 1);
  const previousStart = subDays(previousEnd, days - 1);

  return { from: toDateKey(previousStart), to: toDateKey(previousEnd) };
};

export const computeTrend = ({ current, previous }) => {
  const currentValue = toNumber(current);
  const previousValue = toNumber(previous);

  if (previousValue === 0 && currentValue > 0) {
    return { deltaPercent: null, direction: "up", isNew: true };
  }

  if (previousValue === 0) {
    return { deltaPercent: 0, direction: "flat", isNew: false };
  }

  const deltaPercent = roundTo({
    value: ((currentValue - previousValue) / previousValue) * 100,
    decimals: 1,
  });

  let direction = "flat";
  if (deltaPercent > 0) direction = "up";
  else if (deltaPercent < 0) direction = "down";

  return { deltaPercent, direction, isNew: false };
};

export const rankBy = ({ map, limit, valueKey = "spendUSD" }) => {
  const rows = Object.entries(map || {})
    .map(([id, value]) => ({ id, ...value }))
    .filter((row) => toNumber(row[valueKey]) > 0)
    .sort((a, b) => toNumber(b[valueKey]) - toNumber(a[valueKey]));

  return typeof limit === "number" ? rows.slice(0, limit) : rows;
};

export const isBeforeTrackingStart = ({ from, trackingStartDate }) => {
  if (from == null) return true;
  return String(from) < String(trackingStartDate);
};

export const getChartGranularity = ({ from, to, dailyMaxDays }) => {
  if (!from || !to) return "monthly";
  const days =
    differenceInCalendarDays(parseDateKey(to), parseDateKey(from)) + 1;
  return days > dailyMaxDays ? "monthly" : "daily";
};

// Monthly series nodes cover whole months, so partial edge months of a range
// are rebuilt from the daily series to keep the chart equal to the KPI total.
export const buildMonthlySeries = ({ monthlySeries, dailySeries }) => {
  const dailyByMonth = {};
  Object.entries(dailySeries || {}).forEach(([dateKey, entry]) => {
    const monthKey = toMonthKey(dateKey);
    if (!dailyByMonth[monthKey]) dailyByMonth[monthKey] = [];
    dailyByMonth[monthKey].push(entry);
  });

  const series = { ...(monthlySeries || {}) };
  Object.entries(dailyByMonth).forEach(([monthKey, entries]) => {
    series[monthKey] = mergeRollups({
      rollups: [series[monthKey], ...entries],
    });
  });
  return series;
};

const enumerateKeys = ({ from, to, granularity }) => {
  if (!from || !to || from > to) return [];

  const keys = [];
  if (granularity === "monthly") {
    let cursor = parseDateKey(`${toMonthKey(from)}-01`);
    const end = parseDateKey(`${toMonthKey(to)}-01`);
    while (cursor <= end) {
      keys.push(format(cursor, "yyyy-MM"));
      cursor = addMonths(cursor, 1);
    }
    return keys;
  }

  let cursor = parseDateKey(from);
  const end = parseDateKey(to);
  while (cursor <= end) {
    keys.push(toDateKey(cursor));
    cursor = addDays(cursor, 1);
  }
  return keys;
};

export const buildChartData = ({
  series,
  granularity,
  from,
  to,
  platforms = [],
}) => {
  const source = series || {};
  const sortedKeys = Object.keys(source).sort();
  const resolvedFrom = from || sortedKeys[0] || null;
  const resolvedTo = to || sortedKeys[sortedKeys.length - 1] || null;
  if (!resolvedFrom || !resolvedTo) return [];

  return enumerateKeys({
    from: resolvedFrom,
    to: resolvedTo,
    granularity,
  }).map((key) => {
    const row = { label: key, key };
    platforms.forEach((platform) => {
      row[platform] = toNumber(source[key]?.byPlatform?.[platform]?.spendUSD);
    });
    return row;
  });
};

const sharePercentOf = ({ value, total }) => {
  if (!total) return 0;
  return roundTo({ value: (toNumber(value) / total) * 100, decimals: 1 });
};

const unassignedSpendOf = (rollup) => {
  const assigned = Object.values(rollup?.byCountry || {}).reduce(
    (sum, entry) => sum + toNumber(entry?.spendUSD),
    0
  );
  const unassigned = roundTo({
    value: toNumber(rollup?.totals?.spendUSD) - assigned,
    decimals: 2,
  });
  return unassigned > 0 ? unassigned : 0;
};

// `comparison` is the selected range and the period just before it.
// Historic passes null, so every trend stays flat and the UI hides it.
const trendFor = ({ comparison, pick }) =>
  computeTrend({
    current: pick(comparison?.current),
    previous: pick(comparison?.previous),
  });

export const buildPlatformShare = ({ current, comparison, platforms = [] }) => {
  const totalSpendUSD = toNumber(current?.totals?.spendUSD);
  return platforms
    .map((platform) => {
      const spendUSD = toNumber(current?.byPlatform?.[platform]?.spendUSD);
      return {
        platform,
        spendUSD,
        sharePercent: sharePercentOf({ value: spendUSD, total: totalSpendUSD }),
        trend: trendFor({
          comparison,
          pick: (rollup) => rollup?.byPlatform?.[platform]?.spendUSD,
        }),
      };
    })
    .filter((row) => row.spendUSD > 0);
};

const platformRows = (byPlatform) =>
  Object.entries(byPlatform || {})
    .map(([platform, spendUSD]) => ({ platform, spendUSD: toNumber(spendUSD) }))
    .filter((row) => row.spendUSD > 0)
    .sort((a, b) => b.spendUSD - a.spendUSD);

export const buildTopAdvertiserRows = ({ current, advertisersMeta, limit }) => {
  const totalSpendUSD = toNumber(current?.totals?.spendUSD);
  return rankBy({ map: current?.byAdvertiser, limit }).map((row) => {
    const meta = advertisersMeta?.[row.id];
    return {
      advertiserID: row.id,
      businessName: meta?.businessName ?? row.id,
      businessCountry: meta?.businessCountry ?? null,
      spendUSD: toNumber(row.spendUSD),
      estimatedSpendUSD: toNumber(row.estimatedSpendUSD),
      sharePercent: sharePercentOf({
        value: row.spendUSD,
        total: totalSpendUSD,
      }),
      platforms: platformRows(row.byPlatform),
    };
  });
};

export const buildTopCountryRows = ({ current, limit }) => {
  const totalSpendUSD = toNumber(current?.totals?.spendUSD);
  const rows = rankBy({ map: current?.byCountry, limit }).map((row) => ({
    country: row.id,
    spendUSD: toNumber(row.spendUSD),
    sharePercent: sharePercentOf({ value: row.spendUSD, total: totalSpendUSD }),
    platforms: platformRows(row.byPlatform),
  }));
  const unassignedSpendUSD = unassignedSpendOf(current);

  return {
    rows,
    unassignedSpendUSD,
    unassignedSharePercent: sharePercentOf({
      value: unassignedSpendUSD,
      total: totalSpendUSD,
    }),
  };
};

const countWithSpend = ({ map }) =>
  Object.values(map || {}).filter((entry) => toNumber(entry?.spendUSD) > 0)
    .length;

export const buildKpis = ({ current, comparison }) => {
  const totalSpendUSD = toNumber(current?.totals?.spendUSD);
  const estimatedSpendUSD = toNumber(current?.totals?.estimatedSpendUSD);
  return {
    totalSpendUSD,
    totalTrend: trendFor({
      comparison,
      pick: (rollup) => rollup?.totals?.spendUSD,
    }),
    estimatedSpendUSD,
    previousTotalSpendUSD: toNumber(comparison?.previous?.totals?.spendUSD),
    advertisersWithSpend: countWithSpend({ map: current?.byAdvertiser }),
    advertisersTrend: trendFor({
      comparison,
      pick: (rollup) => countWithSpend({ map: rollup?.byAdvertiser }),
    }),
    previousAdvertisersWithSpend: countWithSpend({
      map: comparison?.previous?.byAdvertiser,
    }),
    countriesWithSpend: countWithSpend({ map: current?.byCountry }),
    countriesTrend: trendFor({
      comparison,
      pick: (rollup) => countWithSpend({ map: rollup?.byCountry }),
    }),
    previousCountriesWithSpend: countWithSpend({
      map: comparison?.previous?.byCountry,
    }),
  };
};
